import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  MapPin,
  Mail,
  Phone,
  MessageSquare,
  Send,
  Instagram,
  User,
  Building,
  MessageCircle,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  ArrowDownLeft
} from 'lucide-react';
import LoadingSpinner, { FormLoadingOverlay, PageLoader } from '../components/ui/LoadingSpinner';
import { Forms } from '../lib/Forms';

const Contact = () => {
  const [formSubmissionLoading, setFormSubmissionLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);


  // Validation schema with Yup - all fields required except company
  const validationSchema = Yup.object({
    name: Yup.string()
      .min(2, 'Name must be at least 2 characters')
      .max(50, 'Name must be less than 50 characters')
      .required('Name is required'),
    company: Yup.string()
      .min(2, 'Company name must be at least 2 characters')
      .max(100, 'Company name must be less than 100 characters'),
    phone: Yup.string()
      .matches(/^[+]?[\d\s\-\(\)]+$/, 'Invalid phone number format')
      .min(10, 'Phone number must be at least 10 digits')
      .required('Phone number is required'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    about: Yup.string()
      .min(5, 'About must be at least 5 characters')
      .max(100, 'About must be less than 100 characters')
      .required('About is required'),
  });

  // Initial form values
  const initialValues = {
    name: '',
    company: '',
    phone: '',
    email: '',
    about: '',
  };

  // Form submission handler with Supabase integration
  const handleSubmit = async (values, { setSubmitting, resetForm, setStatus }) => {
    try {
      setStatus(null);
      setSubmitting(true);

      // Submit form data to Supabase
      const result = await Forms.submitContactForm(values);
      console.log('result :', result);

      if (result.success) {
        setFormSubmitted(true)
        // Show success message


        // Reset form
        resetForm();

        // Clear success message after 7 seconds
        setTimeout(() => setStatus(null), 7000);
      } else {
        // Show error message
        setStatus({
          type: 'error',
          message: result.message || 'Something went wrong. Please try again.'
        });
      }

    } catch (error) {
      console.error('Form submission error:', error);
      setStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 ">
      <div className="">
        {/* Header Section */}
        <div className="text-center mb-16 animate-fade-in">
          <h1 className="text-4xl md:text-5xl font-bold  mb-4 animate-slide-down">
            Contact Us
          </h1>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto animate-slide-up">
            We would love to hear from you.
          </p>

          {/* Hero Image */}
          <div className="mt-12 flex justify-center">
            <div className="relative">
              <img
                src="/contact/hero.webp"
                alt="Customer support representative"
                className="w-80 "
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse md:grid lg:grid-cols-2 gap-20 items-start rounded-2xl shadow-xl md:p-24 p-4">
          {/* Contact Information */}
          <div className="space-y-8 animate-slide-right h-full w-full">
            <div className="flex flex-col justify-between h-full w-full">
              <div className='h-fit'>
                <h2 className="text-2xl md:text-4xl font-bold mb-6">Let's Work</h2>
                <p className="text-gray-200 max-md:text-sm mb-8">
                  Reach out. Let's build something great.
                </p>
              </div>
              <div className="flex gap-3 flex-wrap h-fit">
                {/* Location */}
                <a href="https://maps.app.goo.gl/LLeUQyL1ZQrBnM2q6" className="block h-fit w-fit" target="_blank">
                  <div className="border border-brand-orange/30 w-fit flex gap-2 items-center rounded-full p-2 flex-shrink-0">
                    <MapPin className="w-5 h-5 text-orange-600" />
                    <p className="text-gray-200 text-sm font-normal">Sainikpuri, Secunderabad</p>
                    <ArrowRight className="w-4 h-4 text-black" />
                  </div>
                </a>

                {/* Email */}
                <a href="mailto:info@boldedgemedia.com" className="block w-fit">
                  <div className="border border-brand-orange/30 w-fit flex gap-2 items-center rounded-full p-2 flex-shrink-0">
                    <Mail className="w-5 h-5 text-orange-600" />
                    <p className="text-gray-200 text-sm font-normal">info@boldedgemedia.com</p>
                    <ArrowRight className="w-4 h-4 text-black" />
                  </div>
                </a>
                <a href="mailto:syedemran22@gmail.com" className="block w-fit">
                  <div className="border border-brand-orange/30 w-fit flex gap-2 items-center rounded-full p-2 flex-shrink-0">
                    <Mail className="w-5 h-5 text-orange-600" />
                    <p className="text-gray-200 text-sm font-normal">syedemran22@gmail.com</p>
                    <ArrowRight className="w-4 h-4 text-black" />
                  </div>
                </a>

                {/* Phone */}
                <a href="tel:+918977152054" className="block w-fit">
                  <div className="border border-brand-orange/30 w-fit flex gap-2 items-center rounded-full p-2 flex-shrink-0">
                    <Phone className="w-5 h-5 text-orange-600" />
                    <p className="text-gray-200 text-sm font-normal">+91 89771 52054</p>
                    <ArrowRight className="w-4 h-4 text-black" />
                  </div>
                </a>

                {/* Divider */}
                <div className="h-[1px] w-full bg-brand-orange/40"></div>

                {/* Social Media */}
                <a
                  href="#"
                  className="block w-fit"
                  target="_blank"
                  aria-label="Follow us on Instagram"
                >
                  <div className="border border-brand-orange/30 w-fit flex gap-2 items-center rounded-full p-2 flex-shrink-0">
                    <Instagram className="w-5 h-5 text-orange-600" />
                    <p className="text-gray-200 text-sm font-normal">Follow us on Instagram</p>
                    <ArrowRight className="w-4 h-4 text-black" />
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="w-full">
            <div className="mb-8 flex justify-between gap-4">
              <p className=" text-lg font-semibold">
                Fill out the form and<br /> we will contact you
              </p>
              <ArrowDownLeft className='text-brand-orange' />
            </div>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, status, values }) => (
                <>
                  {/* Status Messages */}
                  {formSubmitted && (
                    <div className={`mb-6 p-6 rounded-lg flex items-start transition-all duration-300 ${formSubmitted
                      ? ' border border-brand-orange/50 '
                      : ' border border-red-200 '
                      }`}>
                      {formSubmitted === 'success' ? (
                        <CheckCircle className="w-6 h-6 mr-3 flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="w-6 h-6 mr-3 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className="font-medium text-base">Thank you for submitting the form, our team will reach out to you within 24 hours.</p>
                      </div>
                    </div>
                  )}
                  {!formSubmitted &&
                    <Form className="space-y-6">
                      {/* Name and Company Row */}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="form-group">
                          <Field
                            id="name"
                            name="name"
                            type="text"
                            placeholder="Your Name *"
                          />
                          <ErrorMessage
                            name="name"
                            component="div"
                            className="mt-1 text-xs text-red-600 animate-shake"
                          />
                        </div>

                        <div className="form-group">
                          <Field
                            id="company"
                            name="company"
                            type="text"
                            placeholder="Company Name (Optional)"
                          />
                          <ErrorMessage
                            name="company"
                            component="div"
                            className="mt-1 text-xs text-red-600 animate-shake"
                          />
                        </div>
                      </div>

                      {/* Phone and Email Row */}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="form-group">
                          <Field
                            id="phone"
                            name="phone"
                            type="tel"
                            placeholder="Your Phone Number *"
                          />
                          <ErrorMessage
                            name="phone"
                            component="div"
                            className="mt-1 text-xs text-red-600 animate-shake"
                          />
                        </div>

                        <div className="form-group">
                          <Field
                            id="email"
                            name="email"
                            type="email"
                            placeholder="Your Email *"
                          />
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="mt-1 text-xs text-red-600 animate-shake"
                          />
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="form-group">
                        <Field
                          id="about"
                          name="about"
                          type="text"
                          placeholder="What's this about? *"
                        />
                        <ErrorMessage
                          name="about"
                          component="div"
                          className="mt-1 text-xs text-red-600 animate-shake"
                        />
                      </div>

                      {/* Submit Button */}
                      <div className="flex justify-center pt-4">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full mx-auto orange flex justify-center items-center"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="animate-spin h-5 w-5 mr-2" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <Send className="w-5 h-5 mr-2" />
                              Send Message
                            </>
                          )}
                        </button>
                      </div>
                    </Form>
                  }
                </>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </section >
  );
};

export default Contact;